import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useGetStockDetailsQuery, useCreateStockMutation } from '../slices/stocksApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const StocksEditScreen = () => {
  const { id: stocksId } = useParams(); // Retrieve stock ID
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [stockDate, setStockDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: stockData, isLoading: loadingStock, error: stockError } = useGetStockDetailsQuery(stocksId);
  const { data: customersData, isLoading: loadingCustomers, error: customersError } = useGetCustomersQuery({ keyword: customerSearch });
  const [createStock, { isLoading: savingStock, }] = useCreateStockMutation();

  // Load initial stock data into the form
  useEffect(() => {
    if (stockData) {
      setSelectedCustomer(stockData.customerDetails || null);
      setNote(stockData.note || '');
      setStockDate(stockData.date ? stockData.date.split('T')[0] : new Date().toISOString().split('T')[0]);
      dispatch(clearCartItems());
      stockData.stockItems.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product, // Ensure product ID is preserved
            qty: item.qty,
          })
        );
      });
    }
  }, [stockData, dispatch]);

  // Filter customers based on search input
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const increaseQtyHandler = (item) => {
    dispatch(
      addToCart({
        ...item,
        qty: item.qty + 1,
      })
    );
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(
        addToCart({
          ...item,
          qty: item.qty - 1,
        })
      );
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const placeStockHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      // Construct stocksData payload for submission
      const stocksData = {
        stockItems: cartItems.map((item) => ({
          product: item.product, // Include product ID
          name: item.name,
          qty: item.qty,
          price: item.price,
          images: item.images[0], // Include first image
        })),
        customerDetails: selectedCustomer._id,
        note,
        date: stockDate,
      };

      // Submit as a new stock record
      const response = await createStock(stocksData).unwrap();
      console.log('Response:', response); // Debug backend response
      dispatch(clearCartItems());
      toast.success('Stock successfully recorded');
      navigate('/house/HomeScreen'); // Redirect after success
    } catch (err) {
      console.error('Error:', err); // Debug error
      toast.error(err?.data?.message || 'Failed to record stocks');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Edit Stock</h1>
        {loadingStock ? (
          <Loader />
        ) : stockError ? (
          <Message variant="danger">{stockError?.data?.message || 'Failed to load stock data'}</Message>
        ) : (
          <>
            {cartItems.length === 0 ? (
              <Message>
                Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
              </Message>
            ) : (
              <ListGroup variant="flush">
                {cartItems.map((item) => (
                  <ListGroup.Item key={item._id}>
                    <Row>
                      <Col md={2}>
                        {item.images?.length > 0 && (
                          <img
                            src={item.images[0]}
                            alt="Product Thumbnail"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      </Col>
                      <Col>{item.name}</Col>
                      <Col md={2}>${item.price.toFixed(2)}</Col>
                      <Col md={3}>
                        <div className="d-flex align-items-center">
                          <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                            <FaMinus />
                          </Button>
                          <span className="mx-2">{item.qty}</span>
                          <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                            <FaPlus />
                          </Button>
                        </div>
                      </Col>
                      <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                      <Col md={1}>
                        <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Col>
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Customer</h2>
              {loadingCustomers ? (
                <Loader />
              ) : customersError ? (
                <Message variant="danger">{customersError?.data?.message || 'Failed to load customers'}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>
                  {filteredCustomers.length > 0 && (
                    <ListGroup>
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serial}
                      </p>
                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="stockDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={stockDate}
                          onChange={(e) => setStockDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={savingStock || cartItems.length === 0 || !selectedCustomer}
                onClick={placeStockHandler}
              >
                {savingStock ? 'Processing...' : 'Submit Stock'}
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StocksEditScreen;